import * as CL from '@design-system/component-library';
import { type OpenFormAnswers } from '../../OpenFormAnswers.js';
import { OpenFormChargeInput } from '../../OpenFormComponents/OpenFormChargeInput.js';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { type OpenFormSummary } from '../../OpenFormHooks/useOpenFormSummary.js';
import {
  eurosPerMonthMsg,
  monthlyChargeMsg,
  oneTimePaymentMsg,
  productsMsg,
  t,
  totalMsg,
} from '../../../../common/i18n/index.js';
import { formatSumToString } from '../../../../common/utils/priceUtils.js';
import { onBlurPrice, onChangePrice } from '../../OpenFormUtils.js';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';

export const OpenFormSummaryProductTable = ({
  answers,
  disabled,
  summary: { products = [], services = [] },
}: {
  answers: OpenFormAnswers;
  disabled: boolean;
  summary: OpenFormSummary;
}) => {
  const { setValue } = useFormContext();

  const rows = useMemo(() => {
    return products.concat(services).flatMap(({ choice, oneOffCharge, productName, recurringCharge }) => {
      const [question, charges] = answers.findOf('charges', context => choice in context.charges);
      if (charges) {
        const defaultOneTime = formatSumToString(oneOffCharge, true);
        const defaultMonthly = formatSumToString(recurringCharge, true);
        return {
          priceOneTime: onChangePrice(charges[choice].one_time_charge__c || defaultOneTime),
          priceMonthly: onChangePrice(charges[choice].recurring_charge__c || defaultMonthly),
          productName: productName,
          setOneTime: (value: string, format: (value?: string) => string) => {
            charges[choice].one_time_charge__c = format(value || defaultOneTime);
            setValue(question + '.charges', charges);
          },
          setMonthly: (value: string, format: (value?: string) => string) => {
            charges[choice].recurring_charge__c = format(value || defaultMonthly);
            setValue(question + '.charges', charges);
          },
        };
      }
      return [];
    });
  }, [answers, products, services, setValue]);

  const totalOneTime = useMemo(() => rows.reduce((sum, row) => sum + Number(row.priceOneTime), 0).toFixed(2), [rows]);
  const totalMonthly = useMemo(() => rows.reduce((sum, row) => sum + Number(row.priceMonthly), 0).toFixed(2), [rows]);

  return !rows.length ? null : (
    <OpenFormGridRow>
      <OpenFormGridCol colWidth={9}>
        <CL.Table
          className="of-openform__view__summary__product-table"
          columns={[
            { key: 'product', label: t.OT3E(productsMsg) },
            { key: 'oneTime', label: t.ASEI(oneTimePaymentMsg), align: 'right' },
            { key: 'monthly', label: t.P0PS(monthlyChargeMsg), align: 'right' },
          ]}
          rows={rows
            .map(({ priceOneTime, priceMonthly, productName, setOneTime, setMonthly }) => ({
              product: <div className="of-openform__view__summary__product-table__name">{productName}</div>,
              oneTime: (
                <OpenFormChargeInput
                  className="of-openform__view__summary__product-table__edit"
                  disabled={disabled}
                  required={true}
                  suffix="€"
                  value={priceOneTime}
                  onBlur={e => setOneTime(e.target.value, onBlurPrice)}
                  onChange={e => setOneTime(e.target.value, onChangePrice)}
                />
              ),
              monthly: (
                <OpenFormChargeInput
                  className="of-openform__view__summary__product-table__edit"
                  disabled={disabled}
                  required={true}
                  suffix={t.H37G(eurosPerMonthMsg)}
                  value={priceMonthly}
                  onBlur={e => setMonthly(e.target.value, onBlurPrice)}
                  onChange={e => setMonthly(e.target.value, onChangePrice)}
                />
              ),
            }))
            .concat({
              product: <div className="of-openform__view__summary__product-table__total">{t.CEQ2(totalMsg)}</div>,
              oneTime: (
                <OpenFormChargeInput
                  className="of-openform__view__summary__product-table__edit of-openform__view__summary__product-table__total"
                  readOnly={true}
                  suffix="€"
                  value={totalOneTime}
                />
              ),
              monthly: (
                <OpenFormChargeInput
                  className="of-openform__view__summary__product-table__edit of-openform__view__summary__product-table__total"
                  readOnly={true}
                  suffix={t.H37G(eurosPerMonthMsg)}
                  value={totalMonthly}
                />
              ),
            })}
        />
      </OpenFormGridCol>
    </OpenFormGridRow>
  );
};
